// Single-panel "replace / substitution" UI shared by the record-based change
// sections (Risk Location, Coverage, Interested Party, Equipment, Tool
// Floater, Fine Art).
//
// One right panel that shows, top to bottom:
//   1. "<Record> to Replace" selector — pick the existing record being removed.
//   2. "New <Record> Details" — the add-record form used to enter the
//      replacement (passed in as children so each section reuses its own form).
//
// Selecting a record to replace persists the removed record's identifying
// snapshot via onSelectToReplace, and the add form's own save handler pushes
// the replacement into the section's records array.

import React from "react";
import { Alert, Box, Grid } from "@mui/material";
import { RightPanel } from "../../../../../../../../components/RightPanel";
import {
  ControlledCustomSelect,
  CustomFormLabel,
} from "../../../../../../../../components/formsComponents";
import { LabelCard } from "../../../../../../../../components/LabelCard/LabelCard";

interface IRecordReplacePanelV2Props {
  open: boolean;
  heading: string;
  // e.g. "Risk Location", "Coverage" — used to label the sections.
  recordLabel: string;
  options: { label: string | number | null; value: string | number }[];
  // Currently selected record being replaced. null until picked.
  valueToReplace: string | number | null;
  // When editing an already-saved change the target is locked in.
  disableSelect?: boolean;
  onSelectToReplace: (value: string | number) => void;
  onClose: () => void;
  // The add-record form for the replacement record.
  children: React.ReactNode;
}

export const RecordReplacePanelV2: React.FC<IRecordReplacePanelV2Props> = ({
  open,
  heading,
  recordLabel,
  options,
  valueToReplace,
  disableSelect = false,
  onSelectToReplace,
  onClose,
  children,
}) => {
  const hasTarget = !!valueToReplace;

  return (
    <RightPanel
      open={open}
      heading={heading}
      subHeading={`Select the ${recordLabel.toLowerCase()} being replaced, then enter the replacement ${recordLabel.toLowerCase()}'s details below.`}
      onClose={onClose}
      width="90%"
    >
      {/* Step 1 (inline): which existing record is being replaced. */}
      <Box sx={{ mb: 2 }}>
        <LabelCard heading={`${recordLabel} to Replace`} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            <CustomFormLabel>{`${recordLabel} to Replace`}</CustomFormLabel>
            <ControlledCustomSelect
              variant="outlined"
              size="small"
              type="string"
              name="record_to_replace"
              fullWidth
              displayEmpty
              value={valueToReplace ?? ""}
              disabled={disableSelect}
              onChange={(e: any) => onSelectToReplace(e.target.value)}
              placeholder="Select One"
              options={options}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Step 2 (inline): the replacement record's details. Gated on a record
          being selected above so the user follows the intended order. */}
      <Box sx={{ mt: 1 }}>
        <LabelCard heading={`New ${recordLabel} Details`} />
        {!hasTarget ? (
          <Alert severity="info" sx={{ mt: 1.5 }}>
            {`Select the ${recordLabel.toLowerCase()} to replace above to enter the new ${recordLabel.toLowerCase()}'s details.`}
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>{children}</Box>
        )}
      </Box>
    </RightPanel>
  );
};
